import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { t } from '../../common/i18n';
import type { JSX, ReactNode } from 'react';

import './HowToOrderASubscription.scss';

type Variant = 'voice' | 'broadband';

interface OrderItem {
  number: string;
  title: string;
  description: ReactNode;
}

const VARIANTS: Record<Variant, OrderItem[]> = {
  voice: [
    {
      number: '1.',
      title: t.ZL7F('Add the subscription to your shopping cart'),
      description: t.A04W(
        `You can choose a new number or transfer your current number. When switching from another operator to Elisa, we'll handle the transfer for you.`
      ),
    },
    {
      number: '2.',
      title: t.UPUX('Create your OmaElisa credentials'),
      description: t.KYYF(
        `To place an order, you need OmaElisa credentials. You'll need signing rights for your company.`
      ),
    },
    {
      number: '3.',
      title: t.F64J('Register with your company name or business ID'),
      description: t.ASJ1(
        'Authenticate easily with online banking credentials or a mobile certificate. Create your user account and finalize your order.'
      ),
    },
    {
      number: '4.',
      title: t.J1EZ('Add services to simplify your everyday work'),
      description: t.CZ7M(
        `During the order process, you can add extra services to your subscription. You can also manage and activate them easily in OmaElisa.`
      ),
    },
  ],
  broadband: [
    {
      number: '1.',
      title: t.ZL7F('Add the subscription to your shopping cart'),
      description: t.T8FJ(
        `Add your selected subscription to the cart and proceed to checkout. To place an order, you need to log in with your OmaElisa credentials or create a new account.`
      ),
    },
    {
      number: '2.',
      title: t.SNSD('Log in or create OmaElisa credentials for your company'),
      description: t.GFYJ(
        `To create new credentials, you’ll need signing rights for your company. Register using your company name or Business ID. Identification can be completed using online banking credentials or a mobile certificate.`
      ),
    },
    {
      number: '3.',
      title: t.YA96('Complete your order'),
      description: (
        <>
          {t.ASJ1(
            'After logging in, you can finalize your order. During the ordering process, you can add useful value-added services to your subscription. You can later manage and activate services in '
          )}
          <Link to={paths.SELF_SERVICE_HOME}>{t.AREA('OmaElisa')}</Link>.
        </>
      ),
    },
  ],
};

const NumberedFeatureBox = ({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: ReactNode;
}): JSX.Element => (
  <div className={`${dsClass.BACKGROUND_COLOR_NEUTRAL_200} of-how-to-order-a-subscription__numbered-feature-box`}>
    <h3 className="of-how-to-order-a-subscription__numbered-feature-box__header">
      <span>{number}</span>
      {title}
    </h3>
    <p className={dsClass.TEXT_S}>{description}</p>
  </div>
);

export const HowToOrderASubscription = ({ variant = 'voice' }: { variant?: Variant } = {}): JSX.Element => {
  const items = VARIANTS[variant];

  return (
    <section className={`of-how-to-order-a-subscription of-how-to-order-a-subscription--${variant}`}>
      <h2>{t.U5GY('How to order a subscription')}</h2>
      <div className="of-how-to-order-a-subscription__grid">
        {items.map(item => (
          <NumberedFeatureBox
            key={item.number}
            number={item.number}
            title={item.title}
            description={item.description}
          />
        ))}
      </div>
    </section>
  );
};
